import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2 } from 'lucide-react';
import { useSessionJwt } from '../../hooks/useSessionJwt';
import { components, typography, palette } from '../../utils/designTokens';
import {
  KPIIcon,
} from '../../components';
import RcaDrawer from './rca/RcaDrawer';
import NoteModal from './rca/NoteModal';
import WorkflowEvalSuggestionsDrawer from './workflows/WorkflowEvalSuggestionsDrawer';
import { getMockAppOverview, getMockClassifierTrends, getMockActionableProblems, getDemoSignalDefinitions } from '../../services/mockDemoData';
import { useAppOverview, useWorkflowDistribution } from '../../services/appDetailApi';
import {
  buildClassifierDefinitionsIndex,
  classifierLabel,
  formatClassifierValue,
  getClassifierDefinition,
  getClassifierSubtypeDefinition,
  useClassifierDefinitions,
  useClassifierOverview,
} from '../../services/classifiersApi';
import { fetchProblems, submitProblemFeedback, submitProblemResolution } from '../../services/problemRcaApi';
import ActionableSignalsTable from '../../components/signals/ActionableSignalsTable';
import WorkflowDistributionPanel from '../../components/signals/WorkflowDistributionPanel';
import { buildSignalScorecard } from '../../utils/signalInsights';
import { LOADING_MESSAGE } from '../../utils/strings';

const ERROR_CLASSIFIER_KEY = 'error_detection';
const PROBLEM_CATEGORY_FALLBACK_TAGS = {
  loop_detection: ['agentic'],
  agent_loop: ['agentic'],
  workflow_anomaly: ['agentic'],
  hallucination: ['quality'],
  wrong_return_policy: ['quality'],
  policy_violation: ['quality'],
  prompt_injection: ['safety'],
  data_exfiltration: ['safety'],
  pii_leakage: ['safety'],
};

// Mirrors _OUTCOME_TO_AGG_COL in workers/src/workflow/clickhouse_client.py — the
// source of truth for which raw outcome_final values roll up into the
// not-completed/completed/pending buckets shown on the Top Business Tasks table. "(none)" is
// the sentinel the traces API maps back to the empty-string (untagged) value.
// pending also has to include 'unknown': _OUTCOME_TO_AGG_COL.get(outcome_final,
// 'pending_count') falls back to pending for ANY unmapped value, and
// workflow_detector.py:406 writes exactly 'unknown' when no classifier outcome
// exists — the far more common case than a genuinely empty outcome_final.
const OUTCOME_BUCKET_VALUES = {
  failure: ['failure'],
  success: ['success', 'acknowledged', 'information'],
  pending: ['(none)', 'unknown'],
};

const toCount = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatRelativeDate = (value) => {
  if (!value) return '-';
  const time = new Date(value).getTime();
  if (!Number.isFinite(time)) return '-';
  const diffMs = Date.now() - time;
  if (diffMs < 60 * 1000) return 'Just now';
  const minutes = Math.floor(diffMs / (60 * 1000));
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(value).toLocaleDateString([], { month: 'short', day: 'numeric' });
};

const rowLatest = (value) => ({
  latest: formatRelativeDate(value),
  latestTitle: value ? new Date(value).toLocaleString() : '',
});

const CompactProblemMetric = ({ card }) => (
  <div
    className={`${components.card.bg} ${components.card.rounded} flex min-h-[58px] items-center justify-between gap-3 px-4 py-3`}
    style={{ border: `1px solid ${components.card.borderColor}` }}
    title={card.tooltip}
  >
    <div className="flex min-w-0 items-center gap-2">
      <KPIIcon type={card.icon} status="neutral" />
      <span className={`${typography.size.xs} ${typography.weight.semibold} truncate uppercase tracking-wide`} style={{ color: palette.text.supporting }}>
        {card.label}
      </span>
    </div>
    <span className={`${typography.size.xl} ${typography.weight.bold} shrink-0 tabular-nums`} style={{ color: palette.text.primary }}>
      {toCount(card.total).toLocaleString()}
    </span>
  </div>
);

const EvaluatedTrafficNote = ({ sessions, traces, onSessionsClick, onTracesClick }) => {
  const hasSessions = sessions != null;
  const hasTraces = traces != null;
  if (!hasSessions && !hasTraces) return null;

  return (
    <span style={{ color: palette.text.muted }}>
      Evaluated{' '}
      {hasSessions && (
        <>
          <button
            type="button"
            className="border-0 bg-transparent p-0 font-medium underline-offset-2 hover:underline"
            style={{ color: palette.accents.primary.base }}
            onClick={onSessionsClick}
          >
            {toCount(sessions).toLocaleString()} sessions
          </button>
          {hasTraces ? ' and ' : ''}
        </>
      )}
      {hasTraces && (
        <button
          type="button"
          className="border-0 bg-transparent p-0 font-medium underline-offset-2 hover:underline"
          style={{ color: palette.accents.primary.base }}
          onClick={onTracesClick}
        >
          {toCount(traces).toLocaleString()} traces
        </button>
      )}
      {' '}in this range.
    </span>
  );
};

const problemIdentity = (problem) => (
  problem?.problem_id
  || [
    problem?.classifier_key,
    problem?.primary_subtype,
    problem?.primary_event_key,
    JSON.stringify(problem?.signature?.components || {}),
  ].filter(Boolean).join(':')
);

const problemClassifierTags = (problem, definitionsIndex) => {
  const classifierKey = problem?.classifier_key || '';
  const definition = getClassifierDefinition(definitionsIndex, classifierKey);
  const tags = Array.isArray(definition?.tags)
    ? definition.tags
    : PROBLEM_CATEGORY_FALLBACK_TAGS[classifierKey] || [];
  return tags.map(tag => `${tag}`.toLowerCase());
};

const buildProblemSummaryCards = (problems, definitionsIndex) => {
  const uniqueProblems = new Map();
  (Array.isArray(problems) ? problems : []).forEach((problem) => {
    const key = problemIdentity(problem);
    if (key) uniqueProblems.set(key, problem);
  });

  const totals = {
    problems: uniqueProblems.size,
    errors: 0,
    quality: 0,
    agentic: 0,
    safety: 0,
  };

  uniqueProblems.forEach((problem) => {
    const classifierKey = problem?.classifier_key || '';
    const tags = problemClassifierTags(problem, definitionsIndex);
    if (classifierKey === ERROR_CLASSIFIER_KEY) totals.errors += 1;
    if (tags.includes('quality')) totals.quality += 1;
    if (tags.includes('agentic')) totals.agentic += 1;
    if (tags.includes('safety')) totals.safety += 1;
  });

  return [
    {
      key: 'problems',
      label: 'Problems',
      icon: 'quality',
      total: totals.problems,
      tooltip: 'Unique problem groups in the selected range',
    },
    {
      key: 'errors',
      label: 'Errors',
      icon: 'reliability',
      total: totals.errors,
      tooltip: 'Unique error-related problems',
    },
    {
      key: 'quality',
      label: 'Quality',
      icon: 'quality',
      total: totals.quality,
      tooltip: 'Unique response-quality problems',
    },
    {
      key: 'agentic',
      label: 'Agentic',
      icon: 'agents',
      total: totals.agentic,
      tooltip: 'Unique agent-behavior problems',
    },
    {
      key: 'safety',
      label: 'Safety',
      icon: 'security',
      total: totals.safety,
      tooltip: 'Unique safety-related problems',
    },
  ];
};

const optimisticProblemFeedback = (problem, vote) => {
  const feedback = problem?.feedback || {};
  const previousVote = feedback.current_user_vote || '';
  const nextVote = vote === 'thumbs_down' ? 'problem_thumbs_down' : 'problem_thumbs_up';
  const previousUp = previousVote === 'problem_thumbs_up' || previousVote === 'thumbs_up';
  const previousDown = previousVote === 'problem_thumbs_down' || previousVote === 'thumbs_down';
  const nextUp = nextVote === 'problem_thumbs_up';
  const nextDown = nextVote === 'problem_thumbs_down';
  return {
    ...problem,
    feedback: {
      thumbs_up: Math.max(0, toCount(feedback.thumbs_up) + (nextUp && !previousUp ? 1 : 0) - (!nextUp && previousUp ? 1 : 0)),
      thumbs_down: Math.max(0, toCount(feedback.thumbs_down) + (nextDown && !previousDown ? 1 : 0) - (!nextDown && previousDown ? 1 : 0)),
      current_user_vote: nextVote,
    },
  };
};

const buildActionableSignalRows = ({
  scorecardRows,
  problems,
  workflowDistribution,
  definitionsIndex,
}) => {
  // Build signal metadata lookup from scorecardRows
  const signalMetaByKey = new Map();
  (Array.isArray(scorecardRows) ? scorecardRows : []).forEach((signalRow) => {
    const key = signalRow?.classifierType || signalRow?.key;
    if (!key) return;
    signalMetaByKey.set(key, signalRow);
  });

  // Build workflow lookup
  const workflowBySignal = new Map();
  (Array.isArray(workflowDistribution) ? workflowDistribution : []).forEach((workflow) => {
    (workflow?.topSignals || []).forEach((signal) => {
      const key = signal?.key || signal?.label;
      if (!key) return;
      const count = toCount(signal?.count);
      const current = workflowBySignal.get(key);
      if (!current || count > current.count) {
        workflowBySignal.set(key, { count, workflowName: workflow.workflowName || workflow.canonical_name || '' });
      }
    });
  });

  const fallbackTypeLabel = (problem) => {
    const subtype = formatClassifierValue(problem?.primary_subtype);
    const eventKey = formatClassifierValue(problem?.primary_event_key);
    if (eventKey && (!subtype || eventKey !== subtype)) {
      if (!subtype || subtype.toLowerCase() === 'pii') return eventKey;
      return `${subtype} · ${eventKey}`;
    }
    return subtype || eventKey || '';
  };

  return (Array.isArray(problems) ? problems : []).map((problem) => {
    const classifierKey = problem?.classifier_key;
    const signalRow = signalMetaByKey.get(classifierKey);
    const definition = getClassifierDefinition(definitionsIndex, classifierKey);
    const subtypeDefinition = getClassifierSubtypeDefinition(definition, problem);
    const signalLabel = definition?.name || signalRow?.label || classifierLabel(classifierKey);
    const classifierDescription = definition?.description || signalRow?.description || '';
    const typeLabel = subtypeDefinition?.display_name || fallbackTypeLabel(problem);
    const displaySignalLabel = typeLabel || signalLabel;
    const signalDescription = typeLabel && signalLabel !== typeLabel
      ? signalLabel
      : classifierDescription;
    const signalDefinition = {
      signal: {
        name: signalLabel,
        description: classifierDescription,
      },
      type: subtypeDefinition?.type_name ? {
        name: subtypeDefinition.type_name,
        description: subtypeDefinition.subtype_description || '',
        key: subtypeDefinition.type_key || problem?.primary_subtype || '',
      } : null,
      event: subtypeDefinition?.event_name ? {
        name: subtypeDefinition.event_name,
        description: subtypeDefinition.event_description || '',
        key: subtypeDefinition.event_key || problem?.primary_event_key || '',
      } : null,
    };
    const latestRca = problem?.latest_rca || null;
    const isResolved = problem?.state === 'resolved';
    const impactedWorkflow = Array.isArray(problem?.impacted_workflows)
      ? problem.impacted_workflows.find(workflow => workflow?.workflow_name || workflow?.workflow_id)
      : null;
    // Only consulted when impactedWorkflow is null: the find() predicate above guarantees a
    // non-null match carries workflow_name or workflow_id, and both are tried first below.
    const topWorkflow = signalRow?.topWorkflow
      || workflowBySignal.get(classifierKey)?.workflowName
      || '';
    const where = {
      display: impactedWorkflow?.workflow_name
        || impactedWorkflow?.workflow_id
        || topWorkflow
        || '-',
    };

    return {
      id: `problem:${problem.problem_id}`,
      signal: displaySignalLabel,
      signalDescription,
      signalDefinition,
      signalKey: classifierKey,
      state: problem?.state || 'open',
      isResolved,
      signalRow,
      problem,
      rca: latestRca,
      group: {
        problem_id: problem?.problem_id || '',
        primary_subtype: problem?.primary_subtype || '',
        primary_event_key: problem?.primary_event_key || '',
        workflow_name: impactedWorkflow?.workflow_name || '',
        workflow_id: impactedWorkflow?.workflow_id || '',
      },
      finding: latestRca?.title
        || latestRca?.summary
        || typeLabel
        || problem?.primary_event_key
        || problem?.primary_subtype
        || `${signalLabel} problem`,
      evidence: problem?.evidence || problem?.reason || '',
      affected: toCount(problem?.trace_count || problem?.signal_count),
      where,
      evidenceLink: true,
      ...rowLatest(problem?.last_seen || problem?.updated_at || signalRow?.lastSeen),
      actionType: latestRca?.rca_id ? 'view_rca' : (isResolved ? 'view_traces' : 'run_rca'),
      actionLabel: isResolved && !latestRca?.rca_id ? 'View traces' : undefined,
      priority: isResolved ? 60 : (latestRca?.rca_id ? 100 : 90),
    };
  });
};

/**
 * App Overview Tab
 *
 * Displays (same structure as Home page, filtered by app):
 * 1. KPI Cards (4 cards: Quality, Errors, Security, Cost & Latency)
 * 2. Eval Metrics Trend Charts
 * 3. Problems Table (traces with low eval scores, errors, or security issues)
 */
const Overview = ({
  selectedAppId,
  environment,
  timeWindow,
  timeLabel,
  isDemo = false,
}) => {
  const navigate = useNavigate();
  const { sessionId, getJwt } = useSessionJwt();

  // KPI data via hook (v2) or demo mock
  const { appData: liveAppData, loading: liveLoading, error: liveError } = useAppOverview(
    isDemo ? null : selectedAppId
  );
  const appData = isDemo ? getMockAppOverview(selectedAppId) : liveAppData;
  const appDataLoading = isDemo ? false : liveLoading;
  const appDataError = isDemo ? null : liveError;

  const {
    classifierTrends: liveClassifierTrends,
    bucketSize: classifierBucketSize,
    loading: classifiersLoading,
    error: classifiersError,
  } = useClassifierOverview(isDemo ? null : selectedAppId, {
    enabled: !isDemo,
    includeTimeseries: true,
  });
  const { definitionsIndex: apiDefinitionsIndex } = useClassifierDefinitions(isDemo ? null : selectedAppId, {
    enabled: !isDemo,
  });
  // Demo mode loads no definitions from the API, so it uses the demo's copy of the signal contract.
  const definitionsIndex = useMemo(
    () => (isDemo ? buildClassifierDefinitionsIndex(getDemoSignalDefinitions()) : apiDefinitionsIndex),
    [isDemo, apiDefinitionsIndex],
  );

  const classifierTrends = isDemo ? getMockClassifierTrends(selectedAppId) : liveClassifierTrends;

  // Single fetch for workflow-derived panels: distribution and outcome list.
  // In v2 mode this calls /v2/data/services/{id}/workflows once and derives everything.
  const {
    workflowDistribution,
    loading: workflowLoading,
    error: workflowError,
  } = useWorkflowDistribution(selectedAppId, {
    environment,
    isDemo,
    sessionToken: getJwt(),
    timeWindow,
  });

  const [problems, setProblems] = useState([]);
  const [problemsLoading, setProblemsLoading] = useState(false);
  const [problemsError, setProblemsError] = useState(null);
  const [problemActionBusyKey, setProblemActionBusyKey] = useState('');
  const [problemActionError, setProblemActionError] = useState(null);
  const [problemFeedbackModal, setProblemFeedbackModal] = useState({ open: false, row: null, note: '' });
  const [problemResolveModal, setProblemResolveModal] = useState({ open: false, row: null, note: '' });

  const refreshProblems = useCallback(({ signal } = {}) => {
    if (isDemo) {
      setProblems(selectedAppId ? getMockActionableProblems(selectedAppId) : []);
      setProblemsLoading(false);
      setProblemsError(null);
      return Promise.resolve();
    }
    const jwt = getJwt();
    if (!selectedAppId || !environment || !jwt) {
      setProblems([]);
      setProblemsLoading(false);
      setProblemsError(null);
      return Promise.resolve();
    }

    setProblemsLoading(true);
    setProblemsError(null);

    return fetchProblems(selectedAppId, environment, timeWindow, jwt, {
      limit: 100,
      signal,
    })
      .then((response) => {
        setProblems(response?.problems || []);
      })
      .catch((err) => {
        if (err?.name === 'AbortError') return;
        setProblemsError(err?.message || 'Failed to load problems');
        setProblems([]);
      })
      .finally(() => {
        if (!signal?.aborted) setProblemsLoading(false);
      });
  }, [environment, isDemo, selectedAppId, sessionId, getJwt, timeWindow]);

  useEffect(() => {
    const abortController = new AbortController();
    refreshProblems({ signal: abortController.signal });
    return () => abortController.abort();
  }, [refreshProblems]);

  // RCA drawer state (problem-native only).
  const [rcaDrawer, setRcaDrawer] = useState({ open: false, rowId: null, problemId: null, rcaId: null });
  const [workflowEvalDrawer, setWorkflowEvalDrawer] = useState({ open: false, workflow: null });

  const scorecardRows = useMemo(
    () => buildSignalScorecard(classifierTrends, workflowDistribution, classifierBucketSize),
    [classifierTrends, workflowDistribution, classifierBucketSize],
  );

  const tableInputsLoading = classifiersLoading || workflowLoading || problemsLoading;

  const actionableSignalRows = useMemo(() => buildActionableSignalRows({
    scorecardRows,
    problems,
    workflowDistribution,
    definitionsIndex,
  }), [
    scorecardRows,
    problems,
    workflowDistribution,
    definitionsIndex,
  ]);

  const problemSummaryCards = useMemo(
    () => buildProblemSummaryCards(problems, definitionsIndex),
    [problems, definitionsIndex],
  );
  const evaluatedSessions = appData?.kpis?.activity?.total_sessions;
  const evaluatedTraces = appData?.kpis?.activity?.total_traces;
  const hasEvaluatedTraffic = evaluatedSessions != null || evaluatedTraces != null;

  const navigateToScopedPage = useCallback((page) => {
    const params = new URLSearchParams();
    if (environment) params.set('env', environment);
    if (timeWindow) params.set('range', timeWindow);
    const query = params.toString();
    navigate(`/app/${selectedAppId}/${page}${query ? `?${query}` : ''}`);
  }, [environment, navigate, selectedAppId, timeWindow]);

  const navigateToSignalTraces = useCallback((signalRow, options = {}, group = null, fallbackSignalKey = null) => {
    const params = new URLSearchParams();
    if (environment) params.set('env', environment);

    // Problem drilldown: scope by problem_id, keeping the current time window.
    // A problem is a signature-level, cross-workflow aggregate. Its member traces
    // span multiple workflows and are mostly workflow-less (the representative trace
    // has none either), so layering classifier/subtype/event_key/workflow filters on
    // top excludes almost all of them. But problem_id is a persistent signature bucket
    // that accumulates matching traces across all time, so we keep the current range
    // rather than widening (otherwise the drilldown floods with months of history).
    if (group?.problem_id) {
      if (timeWindow) params.set('range', timeWindow);
      if (options?.startTime) params.set('start_time', options.startTime);
      if (options?.endTime) params.set('end_time', options.endTime);
      params.set('problem_id', group.problem_id);
      params.set('from', 'problems');
      navigate(`/app/${selectedAppId}/traces?${params.toString()}`);
      return;
    }

    // Non-problem signal drilldown: classifier/subtype/workflow scoping still applies.
    const classifierKey = signalRow?.classifierType || fallbackSignalKey;
    if (!classifierKey) return;
    if (timeWindow) params.set('range', timeWindow);
    if (options?.startTime) params.set('start_time', options.startTime);
    if (options?.endTime) params.set('end_time', options.endTime);
    if (signalRow?.polarity) {
      params.set('signal_group', signalRow.polarity === 'positive' ? 'positive' : 'negative');
    }
    if (classifierKey === ERROR_CLASSIFIER_KEY && (group?.primary_subtype || group?.primary_event_key)) {
      params.set('classifier', ERROR_CLASSIFIER_KEY);
      params.set('classifier_status', 'detected');
    } else {
      params.set('signal', classifierKey);
    }
    if (group?.primary_subtype) params.set('subtype', group.primary_subtype);
    if (group?.primary_event_key) params.set('event_key', group.primary_event_key);
    if (group?.workflow_name) params.set('workflow', group.workflow_name);
    if (group?.workflow_id) params.set('workflow_id', group.workflow_id);
    params.set('from', 'problems');
    navigate(`/app/${selectedAppId}/traces?${params.toString()}`);
  }, [environment, timeWindow, navigate, selectedAppId]);

  const handleWorkflowDrilldown = useCallback((workflow) => {
    if (!workflow) return;
    const params = new URLSearchParams();
    if (environment) params.set('env', environment);
    if (timeWindow) params.set('range', timeWindow);
    const workflowName = typeof workflow?.workflowName === 'string' ? workflow.workflowName.trim() : '';
    if (workflowName) {
      // Traces API uses "(none)" for traces without detected workflow.
      params.set('workflow', workflowName.toLowerCase() === 'unknown workflow' ? '(none)' : workflowName);
    }
    params.set('from', 'problems');
    navigate(`/app/${selectedAppId}/traces?${params.toString()}`);
  }, [environment, timeWindow, navigate, selectedAppId]);

  const handleWorkflowSignalDrilldown = useCallback((workflow, signal) => {
    if (!workflow || !signal?.key) return;
    const params = new URLSearchParams();
    if (environment) params.set('env', environment);
    if (timeWindow) params.set('range', timeWindow);
    const workflowName = typeof workflow?.workflowName === 'string' ? workflow.workflowName.trim() : '';
    if (workflowName) {
      // Traces API uses "(none)" for traces without detected workflow.
      params.set('workflow', workflowName.toLowerCase() === 'unknown workflow' ? '(none)' : workflowName);
    }
    if (workflow?.workflowId) params.set('workflow_id', workflow.workflowId);
    // "signal"/"signal_group" are display-only on the Traces page (v2 list_traces
    // doesn't filter on them) — use "classifier", the param that actually drives
    // useClassifierTraceMatches -> /classifiers/{key}/traces server-side filtering.
    params.set('classifier', signal.key);
    params.set('classifier_status', 'detected');
    params.set('from', 'problems');
    navigate(`/app/${selectedAppId}/traces?${params.toString()}`);
  }, [environment, timeWindow, navigate, selectedAppId]);

  const handleWorkflowOutcomeDrilldown = useCallback((workflow, bucket) => {
    const outcomeValues = OUTCOME_BUCKET_VALUES[bucket];
    if (!workflow || !outcomeValues) return;
    const params = new URLSearchParams();
    if (environment) params.set('env', environment);
    if (timeWindow) params.set('range', timeWindow);
    const workflowName = typeof workflow?.workflowName === 'string' ? workflow.workflowName.trim() : '';
    if (workflowName) {
      // Traces API uses "(none)" for traces without detected workflow.
      params.set('workflow', workflowName.toLowerCase() === 'unknown workflow' ? '(none)' : workflowName);
    }
    if (workflow?.workflowId) params.set('workflow_id', workflow.workflowId);
    params.set('outcome', outcomeValues.join(','));
    params.set('from', 'problems');
    navigate(`/app/${selectedAppId}/traces?${params.toString()}`);
  }, [environment, timeWindow, navigate, selectedAppId]);

  const handleWorkflowEvalOpen = useCallback((workflow) => {
    setWorkflowEvalDrawer({ open: true, workflow });
  }, []);

  const closeWorkflowEvalDrawer = useCallback(() => {
    setWorkflowEvalDrawer({ open: false, workflow: null });
  }, []);


  const handleRcaOpen = useCallback((rca = null) => {
    const params = new URLSearchParams();
    if (environment) params.set('env', environment);
    if (timeWindow) params.set('range', timeWindow);
    params.set('tab', 'rca');
    if (rca?.rca_id) params.set('rca_id', rca.rca_id);
    navigate(`/app/${selectedAppId}?${params.toString()}`);
  }, [environment, timeWindow, navigate, selectedAppId]);

  const handleActionableViewTraces = useCallback((row) => {
    // Use problem_id for exact problem membership.
    navigateToSignalTraces(row?.signalRow, {}, row?.group || null, row?.signalKey);
  }, [navigateToSignalTraces]);

  const handleActionableRunRca = useCallback((row) => {
    const problemId = row?.problem?.problem_id || row?.group?.problem_id;
    if (!problemId) return;
    setRcaDrawer({
      open: true,
      rowId: row?.id ?? null,
      problemId,
      rcaId: null,
    });
  }, []);

  const handleActionableViewRca = useCallback((row) => {
    const rcaId = row?.rca?.rca_id || row?.problem?.latest_rca?.rca_id;
    if (rcaId) {
      setRcaDrawer({
        open: true,
        rowId: row?.id ?? null,
        problemId: row?.problem?.problem_id || row?.group?.problem_id || null,
        rcaId,
      });
    }
  }, []);

  const submitProblemFeedbackForRow = useCallback(async (row, vote, note = '') => {
    const problemId = row?.problem?.problem_id || row?.group?.problem_id;
    const jwt = getJwt();
    if (!problemId || !jwt || problemActionBusyKey) return;

    setProblemActionBusyKey(`${row?.id}:feedback`);
    setProblemActionError(null);
    setProblems((current) => current.map((problem) => (
      problem?.problem_id === problemId ? optimisticProblemFeedback(problem, vote) : problem
    )));
    try {
      await submitProblemFeedback(selectedAppId, environment, problemId, jwt, { vote, note });
      await refreshProblems();
    } catch (err) {
      setProblemActionError(err?.message || 'Failed to save problem feedback');
    } finally {
      setProblemActionBusyKey('');
    }
  }, [environment, problemActionBusyKey, refreshProblems, selectedAppId, getJwt, timeWindow]);

  const handleProblemFeedback = useCallback((row, vote) => {
    if (vote === 'thumbs_down') {
      setProblemFeedbackModal({ open: true, row, note: '' });
      return;
    }
    submitProblemFeedbackForRow(row, vote);
  }, [submitProblemFeedbackForRow]);

  const closeProblemFeedbackModal = useCallback(() => {
    if (problemActionBusyKey) return;
    setProblemFeedbackModal({ open: false, row: null, note: '' });
  }, [problemActionBusyKey]);

  const submitProblemThumbsDown = useCallback(async () => {
    const row = problemFeedbackModal.row;
    await submitProblemFeedbackForRow(row, 'thumbs_down', problemFeedbackModal.note);
    setProblemFeedbackModal({ open: false, row: null, note: '' });
  }, [problemFeedbackModal.note, problemFeedbackModal.row, submitProblemFeedbackForRow]);

  const handleResolveProblem = useCallback((row) => {
    setProblemResolveModal({ open: true, row, note: '' });
  }, []);

  const closeProblemResolveModal = useCallback(() => {
    if (problemActionBusyKey) return;
    setProblemResolveModal({ open: false, row: null, note: '' });
  }, [problemActionBusyKey]);

  const submitProblemResolve = useCallback(async () => {
    const row = problemResolveModal.row;
    const note = problemResolveModal.note.trim();
    const problemId = row?.problem?.problem_id || row?.group?.problem_id;
    const jwt = getJwt();
    if (!problemId || !jwt || !note || problemActionBusyKey) return;

    setProblemActionBusyKey(`${row?.id}:resolve`);
    setProblemActionError(null);
    try {
      await submitProblemResolution(selectedAppId, environment, problemId, jwt, { note });
      await refreshProblems();
      setProblemResolveModal({ open: false, row: null, note: '' });
    } catch (err) {
      setProblemActionError(err?.message || 'Failed to resolve problem');
    } finally {
      setProblemActionBusyKey('');
    }
  }, [environment, problemActionBusyKey, problemResolveModal.note, problemResolveModal.row, refreshProblems, selectedAppId, getJwt, timeWindow]);

  const closeRcaDrawer = useCallback(() => {
    setRcaDrawer({ open: false, rowId: null, problemId: null, rcaId: null });
  }, []);

  const handleRcaDrawerSeeFullAnalysis = useCallback(() => {
    if (rcaDrawer.rcaId) handleRcaOpen({ rca_id: rcaDrawer.rcaId });
  }, [rcaDrawer.rcaId, handleRcaOpen]);

  const handleRcaDrawerReady = useCallback((rcaId) => {
    if (!rcaId) return;
    setRcaDrawer((current) => (
      current.open ? { ...current, rcaId } : current
    ));
  }, []);

  if (!appData) {
    return null;
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Problems Summary Section */}
      <div className="flex flex-col gap-4">
        {/* Section Header */}
        <div className="flex items-end gap-3 pb-3 border-b" style={{ borderColor: palette.neutrals.borderSubtle }}>
          <h2
            style={{
              color: palette.text.heading,
              fontSize: '20px',
              fontWeight: 700,
              textTransform: 'none',
              letterSpacing: 'normal',
              marginTop: '12px',
              marginBottom: 0,
            }}
          >
            Problems
          </h2>
          <span className="flex flex-wrap items-center gap-x-1 text-[11px] text-text-neutral">
            <span>{environment} • {timeLabel}</span>
            {hasEvaluatedTraffic && (
              <>
                <span>•</span>
                <EvaluatedTrafficNote
                  sessions={evaluatedSessions}
                  traces={evaluatedTraces}
                  onSessionsClick={() => navigateToScopedPage('sessions')}
                  onTracesClick={() => navigateToScopedPage('traces')}
                />
              </>
            )}
          </span>
        </div>

        {appDataLoading || (problemsLoading && problems.length === 0) ? (
          <div className="text-sm" style={{ color: palette.text.muted }}>
            {LOADING_MESSAGE}
          </div>
        ) : appDataError ? (
          <div className="text-sm" style={{ color: palette.status.danger }}>
            {appDataError}
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))' }}>
              {problemSummaryCards.map((card) => (
                <CompactProblemMetric key={card.key} card={card} />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Signals Section */}
      <div className="flex flex-col mb-4">
        {/* Signal Insights */}
        {(() => {
          const hasSignalData       = scorecardRows.length > 0;
          const hasProblemData      = problems.length > 0 || actionableSignalRows.length > 0;
          const hasDistributionData = workflowDistribution.length > 0;

          if (
            !hasSignalData
            && !hasProblemData
            && !hasDistributionData
            && !workflowLoading
            && !classifiersLoading
            && !problemsLoading
            && !workflowError
            && !classifiersError
            && !problemsError
          ) {
            return (
              <div
                className={`${components.card.bg} ${components.card.rounded} ${components.card.padding}`}
                style={{ border: `1px solid ${components.card.borderColor}` }}
              >
                <p className={typography.size.sm} style={{ color: palette.text.muted }}>
                  No signal data available for the selected time window.
                </p>
              </div>
            );
          }

          return (
            <div className="flex flex-col gap-4">
              <div className="grid gap-4" style={{ gridTemplateColumns: '1fr' }}>
                <ActionableSignalsTable
                  rows={actionableSignalRows}
                  loading={tableInputsLoading && actionableSignalRows.length === 0}
                  error={problemActionError || problemsError || classifiersError}
                  onViewTraces={handleActionableViewTraces}
                  onViewRca={handleActionableViewRca}
                  onRunRca={handleActionableRunRca}
                  onProblemFeedback={handleProblemFeedback}
                  onResolveProblem={handleResolveProblem}
                  busyActionKey={problemActionBusyKey}
                  selectedRowId={rcaDrawer.open ? rcaDrawer.rowId : null}
                />
              </div>

              <WorkflowDistributionPanel
                workflows={workflowDistribution}
                loading={workflowLoading}
                error={workflowError}
                onWorkflowClick={handleWorkflowDrilldown}
                onSignalClick={handleWorkflowSignalDrilldown}
                onOutcomeClick={handleWorkflowOutcomeDrilldown}
                onWorkflowEvalClick={handleWorkflowEvalOpen}
              />
            </div>
          );
        })()}
      </div>

      <RcaDrawer
        isOpen={rcaDrawer.open}
        onClose={closeRcaDrawer}
        isDemo={isDemo}
        problemId={rcaDrawer.problemId}
        rcaId={rcaDrawer.rcaId}
        appId={selectedAppId}
        environment={environment}
        selectedRange={timeWindow}
        onRcaReady={handleRcaDrawerReady}
        onSeeFullAnalysis={handleRcaDrawerSeeFullAnalysis}
      />

      <WorkflowEvalSuggestionsDrawer
        isOpen={workflowEvalDrawer.open}
        onClose={closeWorkflowEvalDrawer}
        isDemo={isDemo}
        appId={selectedAppId}
        environment={environment}
        selectedRange={timeWindow}
        workflow={workflowEvalDrawer.workflow}
      />

      {problemFeedbackModal.open && (
        <NoteModal
          busy={Boolean(problemActionBusyKey)}
          note={problemFeedbackModal.note}
          onNoteChange={(note) => setProblemFeedbackModal((current) => ({ ...current, note }))}
          onClose={closeProblemFeedbackModal}
          onSubmit={submitProblemThumbsDown}
          title="What is wrong with this problem?"
          description="Add a note if the grouping is wrong, not actionable, duplicated, or a false positive."
          placeholder="Optional note about this problem grouping"
          submitLabel="Submit feedback"
          tone="danger"
        />
      )}

      {problemResolveModal.open && (
        <NoteModal
          busy={Boolean(problemActionBusyKey)}
          note={problemResolveModal.note}
          onNoteChange={(note) => setProblemResolveModal((current) => ({ ...current, note }))}
          onClose={closeProblemResolveModal}
          onSubmit={submitProblemResolve}
          title="Describe the fix"
          description="Add a note describing how this problem was fixed. This is recorded on the resolution."
          placeholder="What was changed to fix this?"
          submitLabel="Mark resolved"
          tone="success"
          icon={<CheckCircle2 size={16} />}
          requireNote
        />
      )}
    </div>
  );
};

export default Overview;
